import React from 'react';
import axios from 'axios';

import { useClaim } from '~/components/ClaimContainer';
import WithConfirm from '~/components/ConfirmModal';
import { PERMISSION_ACTIONS, PERMISSION_VERBS } from '~/components/core/Permissions/PermissionUtils';
import { RestrictedPermissions } from '~/components/core/Permissions/RestrictedPermissions';
import type { COLORS } from '~/components/core/StatusChipCore';
import StatusChipCore from '~/components/core/StatusChipCore';
import SortableTable from '~/components/core/Tables/SortableTable';
import Text from '~/components/core/TextComponents/Text';
import useCurrencyFormatter from '~/components/CurrencyFormatterContext';
import { CalledOffIcon } from '~/components/icons';
import InlineIconButton from '~/components/InlineIconButton';
import { serverDateTimeToLocal } from '~/DateTimeUtils';
import type { RecoveryRequestListResponseRecoveryRequestInfo as RecoveryRequest } from '~/server_shared/openapi/data-contracts';
import { RecoveryRequestListResponseRecoveryRequestDerivedStatus as DerivedStatus } from '~/server_shared/openapi/data-contracts';
import { reportAxiosError } from '~/Utils';

import { recoveryRequestCancelUrl, useClaimRecoveryRequests, usePayerNameById } from './useRecoveryRequests';

// Typed over the generated enum, so a status added to the server's vocabulary is a compile error
// here rather than a chip that silently falls back to grey.
const STATUS_COLORS: Record<DerivedStatus, COLORS> = {
  [DerivedStatus.Pending]: 'orange',
  [DerivedStatus.Requested]: 'blue',
  [DerivedStatus.PartiallyPaid]: 'yellow',
  [DerivedStatus.Paid]: 'green',
  [DerivedStatus.Cancelled]: 'grey',
  [DerivedStatus.Expired]: 'grey',
  [DerivedStatus.Failed]: 'red',
};

// The ticket scopes the action to an unpaid request, and these are the two derived statuses that
// say so: a request with money against it reports `partially_paid` or `paid` instead. That is
// deliberately narrower than the cancel route's own gate, which keys on the *stored* status and so
// would also accept a part-paid row - money already attributed belongs to the recovery it created,
// which has its own cancellation path, rather than to a withdrawal at the provider.
const CANCELLABLE_STATUSES: DerivedStatus[] = [DerivedStatus.Pending, DerivedStatus.Requested];

interface DisplayRequestRow extends RecoveryRequest {
  payer_name: string;
  difference: number;
}

/**
 * The claim's recovery requests, above the recoveries they settle into.
 *
 * Not gated on the rollout flag: the flag gates *creating* a request, and a request already sent
 * stays listed - and settles - after it is turned off. An organization that has never sent one
 * reads an empty list and gets nothing rendered at all.
 */
const RecoveryRequestsTable: React.FC = () => {
  const { claim } = useClaim({ claimRequired: true });
  const { currencyFormatter } = useCurrencyFormatter();
  const { data, isLoading, isError, reloadData } = useClaimRecoveryRequests();
  const payerNameById = usePayerNameById();

  const requests = data?.recovery_requests;

  const rows: DisplayRequestRow[] = React.useMemo(
    () =>
      (requests ?? []).map((request) => ({
        ...request,
        payer_name: payerNameById.get(request.payer_contact_id) ?? '-',
        // what the payer still owes against the request
        difference: request.requested_amount - request.received_sum,
      })),
    [requests, payerNameById]
  );

  const handleCancel = async (recoveryRequestId: number) => {
    try {
      await axios.post(recoveryRequestCancelUrl(claim.organization_id, recoveryRequestId));
      await reloadData();
    } catch (error) {
      await reportAxiosError(error);
    }
  };

  const money = (row: DisplayRequestRow, amount: number) => currencyFormatter.format(amount, row.currency ?? undefined);

  const columns = [
    {
      id: 'created_datetime',
      label: 'Date',
      specialCell: (row: DisplayRequestRow) =>
        row.created_datetime ? serverDateTimeToLocal(row.created_datetime) : '-',
    },
    {
      id: 'payer_name',
      label: 'Payer',
    },
    {
      id: 'payable_type',
      label: 'Payable Type',
      disableSort: true,
      specialCell: (row: DisplayRequestRow) => <span className="capitalize">{row.payable_type}</span>,
    },
    {
      id: 'derived_status',
      label: 'Status',
      disableSort: true,
      specialCell: (row: DisplayRequestRow) => (
        <div className="grid gap-4">
          <StatusChipCore status={row.derived_status.replace(/_/g, ' ')} color={STATUS_COLORS[row.derived_status]} />
          {/* the reason the send or the request itself failed is the only thing the adjuster can
              act on, so it is shown rather than left to the API response */}
          {row.failure_reason ? (
            <Text variant={Text.VARIANTS.XS} colorVariant={Text.COLOR_VARIANTS.SECONDARY}>
              {row.failure_reason}
            </Text>
          ) : null}
        </div>
      ),
    },
    {
      id: 'requested_amount',
      label: 'Requested',
      specialCmpFunc: (row1: DisplayRequestRow, row2: DisplayRequestRow) =>
        row1.requested_amount - row2.requested_amount,
      specialCell: (row: DisplayRequestRow) => money(row, row.requested_amount),
    },
    {
      id: 'received_sum',
      label: 'Received',
      specialCmpFunc: (row1: DisplayRequestRow, row2: DisplayRequestRow) => row1.received_sum - row2.received_sum,
      specialCell: (row: DisplayRequestRow) => money(row, row.received_sum),
    },
    {
      id: 'difference',
      label: 'Difference',
      specialCmpFunc: (row1: DisplayRequestRow, row2: DisplayRequestRow) => row1.difference - row2.difference,
      specialCell: (row: DisplayRequestRow) => money(row, row.difference),
    },
    {
      id: 'actions',
      label: '',
      disableSort: true,
      specialCell: (row: DisplayRequestRow) =>
        CANCELLABLE_STATUSES.includes(row.derived_status) ? (
          <RestrictedPermissions action={PERMISSION_ACTIONS.RECOVERY_REQUEST} verb={PERMISSION_VERBS.WRITE}>
            <WithConfirm
              title="Cancel recovery request?"
              contentText={`${money(row, row.requested_amount)} requested from ${row.payer_name} will be withdrawn, and the payer will no longer be able to pay it.`}
              primaryButtonName="Cancel Request"
              shouldCloseOnPrimary
            >
              <InlineIconButton
                icon={CalledOffIcon}
                // names the row it acts on, for the tooltip and - InlineIconButton falls back to
                // it - for the button's accessible name
                tooltipTitle={`Cancel recovery request from ${row.payer_name}`}
                useIconButton
                onClick={() => handleCancel(row.id)}
              />
            </WithConfirm>
          </RestrictedPermissions>
        ) : null,
    },
  ];

  // Nothing to show, and nothing to say about it: a claim with no requests is the norm, and the
  // fetcher already reports a failed read through reportAxiosError - so neither a skeleton that
  // flashes on every Recoveries-tab open nor a second error surface earns its place here.
  if (isLoading || isError || rows.length === 0) {
    return null;
  }

  return (
    <div className="pb-20">
      <Text variant={Text.VARIANTS.SM} weight={Text.WEIGHTS.SEMI_BOLD} className="pb-8">
        Recovery Requests
      </Text>
      <SortableTable rows={rows} columns={columns} defaultOrderColumn={0} order="desc" />
    </div>
  );
};

export default RecoveryRequestsTable;
